import useAuthStore from '../stores/useAuthStore';
import useThemeStore from '../stores/useThemeStore';
import partners from '../data/currentPartner';

/**
 * The signed-in partner's currency, for money labels, prefixes and units.
 *
 * Mirrors how the CRM side resolves it: admin/pages/revenue/MarkupRulesPage.jsx
 * reads the currency off the selected tenant's branch record rather than a
 * constant. The partner portal's equivalent record is the session itself — the
 * login response (PartnerAuthRepository.contextFor) returns `branches[]`, each
 * with a `currency` that the server already falls back to the branch country's
 * default, ordered default-branch-first.
 *
 * A user pinned to one branch (`user.branchCode`) gets that branch's currency; a
 * partner-wide user gets the default branch. `partner` itself carries no
 * currency, so there is nothing more authoritative to read.
 *
 * The seeded partner record answers only when no session branch has a currency —
 * the mock/spec screens are keyed by the same ids, so they keep reading AED for
 * AlDar and QAR for Snoonu instead of falling to a placeholder.
 */
/**
 * The resolution, stopping at `undefined` rather than dollars.
 *
 * Split out from `resolve` so a caller can tell "this session has no currency
 * established" apart from "this session trades in USD". Those are the same
 * string once the `|| 'USD'` tail is applied, and `CurrencyDisplay` needs the
 * difference: it only falls back to a record's own code when the session truly
 * has nothing.
 */
const resolveOrUndefined = (branches, branchCode, activePartnerId) => {
  const list = Array.isArray(branches) ? branches : [];
  const scoped = branchCode ? list.find((b) => b.id === branchCode) : null;
  const preferred = scoped || list.find((b) => b.isDefault) || list[0];
  return preferred?.currency || partners[activePartnerId]?.primaryCurrency || undefined;
};

const resolve = (...args) => resolveOrUndefined(...args) || 'USD';

/**
 * The branch whose currency this session trades in.
 *
 * The branch CHOSEN in the header outranks the user's own: a second branch may
 * trade in another currency, and the moment an operator switches to it every
 * price on screen is that branch's. `user.branchCode` remains the answer for a
 * branch-scoped user, who has nothing to choose.
 */
const useScopedBranchCode = () => {
  const actingBranchCode = useAuthStore((s) => s.actingBranchCode);
  const ownBranchCode = useAuthStore((s) => s.user?.branchCode);
  return actingBranchCode ?? ownBranchCode;
};

export function usePartnerCurrency() {
  const branches = useAuthStore((s) => s.branches);
  const branchCode = useScopedBranchCode();
  const activePartnerId = useThemeStore((s) => s.activePartnerId);

  return resolve(branches, branchCode, activePartnerId);
}

/** As above, but `undefined` when the session establishes no currency. */
export function usePartnerCurrencyOrUndefined() {
  const branches = useAuthStore((s) => s.branches);
  const branchCode = useScopedBranchCode();
  const activePartnerId = useThemeStore((s) => s.activePartnerId);

  return resolveOrUndefined(branches, branchCode, activePartnerId);
}

/**
 * The signed-in partner's country NAME, off the same session branch the
 * currency is read from. `undefined` when the session states none.
 */
export function usePartnerCountry() {
  const branches = useAuthStore((s) => s.branches);
  const branchCode = useScopedBranchCode();
  const list = Array.isArray(branches) ? branches : [];
  const scoped = branchCode ? list.find((b) => b.id === branchCode) : null;
  const preferred = scoped || list.find((b) => b.isDefault) || list[0];
  return preferred?.country || undefined;
}

/** The same value for stores and helpers, which cannot call a hook. */
export function getPartnerCurrency() {
  const { branches, user, actingBranchCode } = useAuthStore.getState();
  return resolve(
    branches,
    actingBranchCode ?? user?.branchCode,
    useThemeStore.getState().activePartnerId,
  );
}

export default usePartnerCurrency;
